import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { FieldRow, LabeledInput, LabeledSelect } from "../formPrimitives";
import { hinweisen } from "../../redesign/bestaetigen";
import { alleDokumente, elementMitId } from "../../windowManager/browserFenster/fensterRegister";

export interface ZuwendungEintrag {
  id: string;
  /** Server-Id (Akte) — die Karte reicht sie beim Speichern durch. */
  dbId?: number;
  /** Ursprüngliche Art (zuwendung/geschenk); fehlt sie, gilt „zuwendung". */
  art?: string;
  richtung: "von_uns" | "an_uns";
  was: string;
  wann: string;
  anlass: string;
}

export type WerLaedtEin = "wir_laden_ein" | "wir_sind_eingeladen";
export type EinladungPrio = "A" | "B" | "C" | "";

export interface EinladungEintrag {
  id: string;
  /** Server-Id (Akte) — die Karte reicht sie beim Speichern durch. */
  dbId?: number;
  werLaedtEin: WerLaedtEin;
  veranstaltung: string;
  wann: string;
  status: string;
  prio: EinladungPrio;
  fileUrl?: string;
  fileName?: string;
  fileRef?: File;
}

const UPLOAD_ICON = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M14 10v2.667A1.333 1.333 0 0 1 12.667 14H3.333A1.333 1.333 0 0 1 2 12.667V10" />
    <path d="M4.667 5.333 8 2l3.333 3.333" />
    <path d="M8 2v8" />
  </svg>
);

const DOKUMENT_ICON = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M9.333 2H4a1.333 1.333 0 0 0-1.333 1.333v9.334A1.333 1.333 0 0 0 4 14h8a1.333 1.333 0 0 0 1.333-1.333V6.667L9.333 2Z" />
    <path d="M9.333 2v4.667H14" />
  </svg>
);

const ENTFERNEN_ICON = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4M6.667 7.333v4.667M9.333 7.333v4.667M12.667 4v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667A1.333 1.333 0 0 1 3.333 13.333V4" />
  </svg>
);

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: "28px",
  },
  subBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  subHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1.5px solid ${REFINED.goldLine}`,
    paddingBottom: "8px",
  },
  subTitle: {
    fontFamily: REFINED.fontUI,
    fontSize: "1.05rem",
    fontWeight: 800,
    color: REFINED.ink,
    margin: 0,
  },
  itemRow: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    position: "relative",
  },
  itemPast: {
    backgroundColor: "#fafafb",
    ...shorthands.border("1px", "solid", "#e5e7eb"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("12px"),
    opacity: 0.92,
  },
  itemTopBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  divider: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("12px", "0"),
    ...shorthands.borderWidth("0"),
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("6px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.80rem",
    padding: "3px 8px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  addBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.86rem",
    padding: "7px 16px",
    cursor: "pointer",
    alignSelf: "flex-end",
    marginTop: "8px",
    transitionProperty: "background-color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  radioGroup: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "20px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    marginTop: "4px",
  },
  radioLabel: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    "& input[type='radio']": {
      accentColor: REFINED.aub,
      cursor: "pointer",
    },
  },
  emptyText: {
    color: REFINED.ink3,
    fontSize: "0.85rem",
    fontFamily: REFINED.fontUI,
    padding: "4px 0",
  },
  fieldLabel: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
  },
  fileActionRow: {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "6px",
  },
  fileBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.border("1px", "solid", "#d4c29d"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontSize: "0.82rem",
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    padding: "6px 12px",
    cursor: "pointer",
    ":hover": { backgroundColor: "#faf8f5" },
  },
  fileLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    maxWidth: "260px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...shorthands.border("1px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    fontSize: "0.82rem",
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    padding: "6px 12px",
    cursor: "pointer",
    ":hover": { textDecoration: "underline" },
  },
  fileDeleteBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    padding: "6px 9px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
});

const PriorityInfoIcon: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const popoverRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    alleDokumente.addEventListener("mousedown", handleClickOutside);
    return () => alleDokumente.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <span
      ref={popoverRef}
      style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: "6px" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Priorität Erklärung"
        title="A = Auf jeden Fall · B = Optional · C = Wenn noch Platz ist"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: open ? REFINED.aub : REFINED.ink3,
          transition: "color 140ms ease",
        }}
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="8" cy="8" r="7" />
          <path d="M8 7v4" />
          <path d="M8 4.5h.01" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: REFINED.white,
            border: `1px solid ${REFINED.line}`,
            borderRadius: "10px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            padding: "10px 14px",
            zIndex: 1000,
            whiteSpace: "nowrap",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            fontSize: "0.82rem",
            fontFamily: REFINED.fontUI,
            color: REFINED.ink,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontWeight: 800, color: "#d13438", backgroundColor: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "1px 6px", fontSize: "0.78rem" }}>A</span>
            <span>Auf jeden Fall</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontWeight: 800, color: "#ea580c", backgroundColor: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "4px", padding: "1px 6px", fontSize: "0.78rem" }}>B</span>
            <span>Optional</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontWeight: 800, color: "#107c10", backgroundColor: "#f0fdf4", border: "1px solid #86efac", borderRadius: "4px", padding: "1px 6px", fontSize: "0.78rem" }}>C</span>
            <span>Wenn noch Platz ist</span>
          </div>
        </div>
      )}
    </span>
  );
};

const PriorityPills: React.FC<{
  value: EinladungPrio;
  onChange: (prio: EinladungPrio) => void;
}> = ({ value, onChange }) => {
  const pillStyle = (
    prio: "A" | "B" | "C",
    activeBg: string,
    inactiveBorder: string,
    inactiveText: string,
  ): React.CSSProperties => {
    const active = value === prio;
    return {
      height: "42px",
      minWidth: "52px",
      padding: "0 18px",
      borderRadius: "10px",
      fontWeight: 800,
      fontSize: "0.95rem",
      fontFamily: REFINED.fontUI,
      cursor: "pointer",
      border: `1.5px solid ${active ? activeBg : inactiveBorder}`,
      backgroundColor: active ? activeBg : "#ffffff",
      color: active ? "#ffffff" : inactiveText,
      boxShadow: active ? `0 2px 4px ${activeBg}40` : "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      transition: "all 140ms ease",
    };
  };

  return (
    <div style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
      <button
        type="button"
        style={pillStyle("A", "#d13438", "#fca5a5", "#d13438")}
        onClick={() => onChange(value === "A" ? "" : "A")}
        title="A = Auf jeden Fall"
      >
        A
      </button>
      <button
        type="button"
        style={pillStyle("B", "#f97316", "#fb923c", "#ea580c")}
        onClick={() => onChange(value === "B" ? "" : "B")}
        title="B = Optional"
      >
        B
      </button>
      <button
        type="button"
        style={pillStyle("C", "#107c10", "#86efac", "#107c10")}
        onClick={() => onChange(value === "C" ? "" : "C")}
        title="C = Wenn noch Platz ist"
      >
        C
      </button>
    </div>
  );
};

const isPastDate = (dateStr: string): boolean => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return d < today;
};

const AN_WEN_OPTIONEN = [
  { value: "von_uns", label: "Von uns an den Kontakt" },
  { value: "an_uns", label: "Von Kontakt an uns" },
];

const STATUS_WIR_LADEN_EIN = [
  { value: "Einladung verschickt", label: "Einladung verschickt" },
  { value: "Zugesagt", label: "Zugesagt" },
  { value: "Abgesagt", label: "Abgesagt" },
  { value: "Teilgenommen", label: "Teilgenommen" },
];

const STATUS_WIR_SIND_EINGELADEN = [
  { value: "Einladung erhalten", label: "Einladung erhalten" },
  { value: "Zugesagt", label: "Zugesagt" },
  { value: "Abgesagt", label: "Abgesagt" },
  { value: "Teilgenommen", label: "Teilgenommen" },
];

export const ZuwendungenAbschnitt: React.FC<{
  zuwendungen: ZuwendungEintrag[];
  onZuwendungenChange: (list: ZuwendungEintrag[]) => void;
  einladungen: EinladungEintrag[];
  onEinladungenChange: (list: EinladungEintrag[]) => void;
  diesenKontaktZuVeranstaltungen: boolean;
  onDiesenKontaktChange: (val: boolean) => void;
  kontaktPrioritaet: EinladungPrio;
  onKontaktPrioritaetChange: (prio: EinladungPrio) => void;
  newsletterEinwilligung: boolean;
  onNewsletterEinwilligungChange: (val: boolean) => void;
  /** Unter Ja/Nein, z. B. die Wahl der E-Mail-Adressen (ZuwendungenKarte). */
  newsletterZusatz?: React.ReactNode;
  /**
   * „Hat der Kontakt einen Newsletter?" (User 28.09.2026). Die Frage erscheint
   * nur, wenn der Aufrufer `eigenerNewsletter` verwaltet (`onEigenerNewsletterChange`).
   * `null` = nicht beantwortet.
   */
  eigenerNewsletter?: {
    hat: boolean | null;
    name: string;
    abonniert: boolean | null;
  };
  onEigenerNewsletterChange?: (patch: {
    hat?: boolean | null;
    name?: string;
    abonniert?: boolean | null;
  }) => void;
  uploadFile?: (file: File) => Promise<{ url: string; name?: string }>;
  onOpenDocument?: (url: string) => void;
}> = ({
  zuwendungen,
  onZuwendungenChange,
  einladungen,
  onEinladungenChange,
  diesenKontaktZuVeranstaltungen,
  onDiesenKontaktChange,
  kontaktPrioritaet,
  onKontaktPrioritaetChange,
  newsletterEinwilligung,
  onNewsletterEinwilligungChange,
  newsletterZusatz,
  eigenerNewsletter,
  onEigenerNewsletterChange,
  uploadFile,
  onOpenDocument,
}) => {
  const s = useStyles();

  // Erinnert die letzte Prio (z. B. vorbelegt durch Kontakt oder letzte Einladung)
  const lastPrioRef = React.useRef<EinladungPrio>(kontaktPrioritaet || "A");
  const [uploadingId, setUploadingId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (kontaktPrioritaet) {
      lastPrioRef.current = kontaktPrioritaet;
    }
  }, [kontaktPrioritaet]);

  // ── Zuwendung & Geschenke ──
  const addZuwendung = () => {
    onZuwendungenChange([
      ...zuwendungen,
      {
        id: "zw-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
        richtung: "von_uns",
        was: "",
        wann: "",
        anlass: "",
      },
    ]);
  };

  const updateZuwendung = (id: string, patch: Partial<ZuwendungEintrag>) => {
    onZuwendungenChange(zuwendungen.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeZuwendung = (id: string) => {
    onZuwendungenChange(zuwendungen.filter((it) => it.id !== id));
  };

  // ── Einladungen ──
  const addEinladung = () => {
    const defaultPrio = kontaktPrioritaet || lastPrioRef.current || "A";
    onEinladungenChange([
      ...einladungen,
      {
        id: "einladung-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
        werLaedtEin: "wir_laden_ein",
        veranstaltung: "",
        wann: "",
        status: "Einladung verschickt",
        prio: defaultPrio,
      },
    ]);
  };

  const updateEinladung = (id: string, patch: Partial<EinladungEintrag>) => {
    if (patch.prio) {
      lastPrioRef.current = patch.prio;
    }
    onEinladungenChange(einladungen.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeEinladung = (id: string) => {
    onEinladungenChange(einladungen.filter((it) => it.id !== id));
  };

  const handleFileUpload = async (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadFile) return;

    setUploadingId(id);
    try {
      const res = await uploadFile(file);
      updateEinladung(id, {
        fileUrl: res.url,
        fileName: res.name || file.name,
        fileRef: file,
      });
    } catch (err) {
      console.error("[Einladung] Upload-Fehler:", err);
      await hinweisen({ titel: "Upload fehlgeschlagen", text: "Die Einladung konnte nicht hochgeladen werden. Bitte noch einmal versuchen.", ton: "danger" });
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className={s.container}>
      {/* ── 1. Zuwendung und Geschenke ── */}
      <div className={s.subBlock}>
        <div className={s.subHeader}>
          <h3 className={s.subTitle}>Zuwendung und Geschenke</h3>
        </div>

        {zuwendungen.length === 0 && (
          <div className={s.emptyText}>Keine Zuwendungen oder Geschenke erfasst.</div>
        )}

        {zuwendungen.map((item, idx) => (
          <div key={item.id} className={s.itemRow}>
            {idx > 0 && <hr className={s.divider} />}
            <FieldRow columns={2}>
              <LabeledSelect
                label="An wen?"
                name={`zw-richtung-${item.id}`}
                value={item.richtung}
                onChange={(v) => updateZuwendung(item.id, { richtung: (v as any) || "von_uns" })}
                options={AN_WEN_OPTIONEN}
              />
              <LabeledInput
                label="Was"
                name={`zw-was-${item.id}`}
                value={item.was}
                onChange={(v) => updateZuwendung(item.id, { was: v })}
                placeholder="z. B. Weinpräsent, Blumenstrauß …"
              />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput
                label="Wann"
                name={`zw-wann-${item.id}`}
                type="date"
                value={item.wann}
                onChange={(v) => updateZuwendung(item.id, { wann: v })}
              />
              <LabeledInput
                label="Anlass"
                name={`zw-anlass-${item.id}`}
                value={item.anlass}
                onChange={(v) => updateZuwendung(item.id, { anlass: v })}
                placeholder="z. B. Geburtstag, Jubiläum …"
              />
            </FieldRow>
            <div className={s.itemTopBar} style={{ marginTop: "4px" }}>
              <button
                type="button"
                className={s.removeBtn}
                onClick={() => removeZuwendung(item.id)}
              >
                Entfernen
              </button>
            </div>
          </div>
        ))}

        <button type="button" className={s.addBtn} style={{ alignSelf: "flex-end" }} onClick={addZuwendung}>
          + Zuwendung/Geschenk hinzufügen
        </button>
      </div>

      {/* ── 2. Einladungen ── */}
      <div className={s.subBlock}>
        <div className={s.subHeader}>
          <h3 className={s.subTitle}>Einladungen</h3>
        </div>

        {/* Kopfzeile: Diesen Kontakt zu Veranstaltungen einladen + Priorität */}
        <FieldRow columns={2}>
          <div style={{ display: "grid", rowGap: "6px" }}>
            <span className={s.fieldLabel}>
              Diesen Kontakt zu Veranstaltungen einladen
            </span>
            <div className={s.radioGroup}>
              <label className={s.radioLabel}>
                <input
                  type="radio"
                  name="diesenKontaktZuVeranstaltungen"
                  checked={diesenKontaktZuVeranstaltungen === true}
                  onChange={() => onDiesenKontaktChange(true)}
                />
                <span>Ja</span>
              </label>
              <label className={s.radioLabel}>
                <input
                  type="radio"
                  name="diesenKontaktZuVeranstaltungen"
                  checked={diesenKontaktZuVeranstaltungen === false}
                  onChange={() => onDiesenKontaktChange(false)}
                />
                <span>Nein</span>
              </label>
            </div>
          </div>

          <div style={{ display: "grid", rowGap: "6px" }}>
            <span className={s.fieldLabel} style={{ display: "inline-flex", alignItems: "center" }}>
              Welche Priorität hat dieser Gast für eine Veranstaltung?
              <PriorityInfoIcon />
            </span>
            <div>
              <PriorityPills
                value={kontaktPrioritaet}
                onChange={(prio) => {
                  onKontaktPrioritaetChange(prio);
                  if (prio) lastPrioRef.current = prio;
                }}
              />
            </div>
          </div>
        </FieldRow>

        {einladungen.length === 0 && (
          <div className={s.emptyText}>Keine Einladungen erfasst.</div>
        )}

        {einladungen.map((item, idx) => {
          const isPast = isPastDate(item.wann);
          const werLaedtEin = item.werLaedtEin || "wir_laden_ein";

          const werOptionen = [
            {
              value: "wir_laden_ein",
              label: isPast ? "Wir luden ein" : "Wir laden ein",
            },
            {
              value: "wir_sind_eingeladen",
              label: isPast ? "Wir waren eingeladen" : "Wir sind eingeladen",
            },
          ];

          return (
            <div
              key={item.id}
              className={mergeClasses(s.itemRow, isPast && s.itemPast)}
            >
              {idx > 0 && <hr className={s.divider} />}

              {/* Zeile 1: Wer lädt ein? */}
              <FieldRow columns={2}>
                <LabeledSelect
                  label="Wer lädt ein?"
                  name={`einladung-wer-${item.id}`}
                  value={werLaedtEin}
                  onChange={(v) =>
                    updateEinladung(item.id, {
                      werLaedtEin: (v as WerLaedtEin) || "wir_laden_ein",
                      status:
                        v === "wir_sind_eingeladen"
                          ? "Einladung erhalten"
                          : "Einladung verschickt",
                    })
                  }
                  options={werOptionen}
                />
                <div />
              </FieldRow>

              {/* Zeile 2: Veranstaltung | Wann? */}
              <FieldRow columns={2}>
                <LabeledInput
                  label="Veranstaltung"
                  name={`einladung-veranstaltung-${item.id}`}
                  value={item.veranstaltung}
                  onChange={(v) => updateEinladung(item.id, { veranstaltung: v })}
                  placeholder={
                    werLaedtEin === "wir_sind_eingeladen"
                      ? "z. B. Jubiläumsgala, Firmenfeier …"
                      : "z. B. Sommerfest, Tivoli Talk …"
                  }
                />
                <LabeledInput
                  label="Wann?"
                  name={`einladung-wann-${item.id}`}
                  type="date"
                  value={item.wann}
                  onChange={(v) => updateEinladung(item.id, { wann: v })}
                />
              </FieldRow>

              {/* Zeile 3: Einladungsstatus | Priorität (bei wir_laden_ein) */}
              {werLaedtEin === "wir_laden_ein" ? (
                <FieldRow columns={2}>
                  <LabeledSelect
                    label="Einladungsstatus"
                    name={`einladung-status-${item.id}`}
                    value={item.status}
                    onChange={(v) => updateEinladung(item.id, { status: v })}
                    options={STATUS_WIR_LADEN_EIN}
                  />
                  <div style={{ display: "grid", rowGap: "7px" }}>
                    <span className={s.fieldLabel} style={{ display: "inline-flex", alignItems: "center" }}>
                      Welche Priorität hat dieser Gast für die Veranstaltung?
                      <PriorityInfoIcon />
                    </span>
                    <div>
                      <PriorityPills
                        value={item.prio}
                        onChange={(prio) => updateEinladung(item.id, { prio })}
                      />
                    </div>
                  </div>
                </FieldRow>
              ) : (
                <>
                  <FieldRow columns={2}>
                    <LabeledSelect
                      label="Einladungsstatus"
                      name={`einladung-status-${item.id}`}
                      value={item.status}
                      onChange={(v) => updateEinladung(item.id, { status: v })}
                      options={STATUS_WIR_SIND_EINGELADEN}
                    />
                    <div />
                  </FieldRow>

                  {/* Zeile 4: Einladung hochladen (Upload) */}
                  <div style={{ marginTop: "4px" }}>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: REFINED.ink,
                        marginBottom: "6px",
                        display: "block",
                      }}
                    >
                      Einladung hochladen
                    </label>
                    <input
                      id={`einladung-file-${item.id}`}
                      type="file"
                      accept="*/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileUpload(item.id, e)}
                    />
                    <div className={s.fileActionRow}>
                      {item.fileUrl || item.fileName ? (
                        <>
                          <button
                            type="button"
                            className={s.fileLink}
                            onClick={() => onOpenDocument?.(item.fileUrl || "")}
                            title={`Einladung öffnen: ${item.fileName || "Einladung"}`}
                          >
                            {DOKUMENT_ICON}
                            <span>{item.fileName || "Einladung"}</span>
                          </button>
                          <button
                            type="button"
                            className={s.fileBtn}
                            disabled={uploadingId === item.id}
                            onClick={() =>
                              elementMitId(`einladung-file-${item.id}`)?.click()
                            }
                          >
                            {UPLOAD_ICON} Ersetzen
                          </button>
                          <button
                            type="button"
                            className={s.fileDeleteBtn}
                            onClick={() =>
                              updateEinladung(item.id, {
                                fileUrl: "",
                                fileName: "",
                                fileRef: undefined,
                              })
                            }
                            title="Einladung entfernen"
                            aria-label="Einladung entfernen"
                          >
                            {ENTFERNEN_ICON}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={s.fileBtn}
                          disabled={uploadingId === item.id}
                          onClick={() =>
                            elementMitId(`einladung-file-${item.id}`)?.click()
                          }
                        >
                          {UPLOAD_ICON}{" "}
                          {uploadingId === item.id ? "Lädt hoch…" : "Einladung hochladen"}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className={s.itemTopBar} style={{ marginTop: "4px" }}>
                <button
                  type="button"
                  className={s.removeBtn}
                  onClick={() => removeEinladung(item.id)}
                >
                  Entfernen
                </button>
              </div>
            </div>
          );
        })}

        <button type="button" className={s.addBtn} style={{ alignSelf: "flex-end" }} onClick={addEinladung}>
          + Einladung hinzufügen
        </button>
      </div>

      {/* ── 3. Newsletter ── */}
      <div className={s.subBlock}>
        <div className={s.subHeader}>
          <h3 className={s.subTitle}>Newsletter</h3>
        </div>
        {onEigenerNewsletterChange && (
          <div style={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
            <span className={s.fieldLabel}>Hat der Kontakt einen Newsletter?</span>
            <div className={s.radioGroup}>
              {([true, false] as const).map((wert) => (
                <label className={s.radioLabel} key={String(wert)}>
                  <input
                    type="radio"
                    name="eigenerNewsletter"
                    checked={eigenerNewsletter?.hat === wert}
                    onChange={() => onEigenerNewsletterChange({ hat: wert })}
                  />
                  <span>{wert ? "Ja" : "Nein"}</span>
                </label>
              ))}
            </div>
            {eigenerNewsletter?.hat === true && (
              <FieldRow columns={2}>
                <LabeledInput
                  label="Welcher Newsletter?"
                  name="eigenerNewsletterName"
                  value={eigenerNewsletter.name}
                  onChange={(v) => onEigenerNewsletterChange({ name: v })}
                  placeholder="z. B. Mitglieder-Rundbrief"
                />
                <div style={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
                  <span className={s.fieldLabel}>Haben wir den Newsletter abonniert?</span>
                  <div className={s.radioGroup}>
                    {([true, false] as const).map((wert) => (
                      <label className={s.radioLabel} key={String(wert)}>
                        <input
                          type="radio"
                          name="eigenenNewsletterAbonniert"
                          checked={eigenerNewsletter.abonniert === wert}
                          onChange={() => onEigenerNewsletterChange({ abonniert: wert })}
                        />
                        <span>{wert ? "Ja" : "Nein"}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </FieldRow>
            )}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
          <span className={s.fieldLabel}>Abonniert der Kontakt unseren Newsletter?</span>
          <div className={s.radioGroup}>
            <label className={s.radioLabel}>
              <input
                type="radio"
                name="newsletterEinwilligung"
                checked={newsletterEinwilligung === true}
                onChange={() => onNewsletterEinwilligungChange(true)}
              />
              <span>Ja</span>
            </label>
            <label className={s.radioLabel}>
              <input
                type="radio"
                name="newsletterEinwilligung"
                checked={newsletterEinwilligung === false}
                onChange={() => onNewsletterEinwilligungChange(false)}
              />
              <span>Nein</span>
            </label>
          </div>
          {newsletterZusatz}
        </div>
      </div>
    </div>
  );
};
